import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTheme } from "styled-components";
import { cn } from "../../../lib/utils";
import { useProfileSetup } from "../../../contexts/ProfileContext";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";

const FILTER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "free", label: "Free" },
];

const FILTER_LABELS = {
  all: "all",
  paid: "paid",
  free: "free",
};

export default function UsersTab() {
  const theme = useTheme();
  const { apiBase, getAuthHeaders } = useProfileSetup();
  const [filter, setFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchUsers() {
      setLoading(true);
      setError(null);
      try {
        const headers = await getAuthHeaders();
        const { data } = await axios.get(`${apiBase}/admin/users`, {
          headers,
          params: { status: filter },
        });
        if (!cancelled) {
          setUsers(Array.isArray(data?.users) ? data.users : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || "Failed to load users");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [apiBase, filter, getAuthHeaders]);

  const filterLabel = useMemo(() => FILTER_LABELS[filter] || "all", [filter]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mt-4 p-4 rounded-md text-sm"
        style={{ backgroundColor: theme.colors.errorBg, color: theme.colors.error }}
      >
        {error}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {users.length} {filterLabel} user{users.length !== 1 ? "s" : ""}
        </p>
        <div className="inline-flex rounded-lg border border-border p-1 bg-muted/30">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setFilter(option.id)}
              className={cn(
                "px-3 py-1.5 text-sm rounded-md transition-colors",
                filter === option.id
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-primary"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="bg-muted/80 backdrop-blur-sm">
                <th className="text-left px-4 py-2.5 font-medium">FirstName</th>
                <th className="text-left px-4 py-2.5 font-medium">LastName</th>
                <th className="text-left px-4 py-2.5 font-medium">Email</th>
                <th className="text-left px-4 py-2.5 font-medium">CompanyName</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    No {filterLabel} users found
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={`${user.email || "user"}-${index}`} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5">{user.firstName || "-"}</td>
                    <td className="px-4 py-2.5">{user.lastName || "-"}</td>
                    <td className="px-4 py-2.5">{user.email || "-"}</td>
                    <td className="px-4 py-2.5">{user.companyName || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
